/**
 * Read-only views over the state that more than one module needs.
 */

import type { CourtGame, Lineup, Pair, Player, PlayerId, Round, TournamentState, Where } from "./types";

export function currentRound(state: TournamentState): Round | null {
  return state.rounds.length ? (state.rounds[state.rounds.length - 1] as Round) : null;
}

export function roundByNo(state: TournamentState, no: number): Round | null {
  return state.rounds.find((r) => r.no === no) ?? null;
}

/** Players who are here and available right now. */
export function availablePlayers(state: TournamentState): PlayerId[] {
  return Object.values(state.players)
    .filter((p) => p.status === "in")
    .map((p) => p.id)
    .sort();
}

export function playersIn(lineup: Lineup): PlayerId[] {
  return lineup.courts.flatMap((c) => [...c.a, ...c.b]);
}

export function presentIn(lineup: Lineup): PlayerId[] {
  return [...playersIn(lineup), ...lineup.sittingOut];
}

export function courtOf(lineup: Lineup, id: PlayerId): CourtGame | null {
  return lineup.courts.find((c) => c.a.includes(id) || c.b.includes(id)) ?? null;
}

export function pairKey(x: PlayerId, y: PlayerId): string {
  return x < y ? `${x}|${y}` : `${y}|${x}`;
}

/** Everything the scheduler and the checks need to know about the past, per player and per pair. */
export interface History {
  /** Rounds each player was in a line-up (voided games still count: they were on court). */
  gamesPlayed: Record<PlayerId, number>;
  /** Rounds each player was present for (playing or sitting out). */
  roundsPresent: Record<PlayerId, number>;
  /** Players who played in the latest round. */
  playedLast: Set<PlayerId>;
  /** Players present but sitting out in the latest round. */
  satOutLast: Set<PlayerId>;
  /** How many times each player has sat out two rounds running while present for both. */
  doubleSits: Record<PlayerId, number>;
  /** Times the pair has been partners. */
  partners: Map<string, number>;
  /** Times the pair has been opponents. */
  opponents: Map<string, number>;
}

export function history(state: TournamentState): History {
  const gamesPlayed: Record<PlayerId, number> = {};
  const roundsPresent: Record<PlayerId, number> = {};
  const doubleSits: Record<PlayerId, number> = {};
  const partners = new Map<string, number>();
  const opponents = new Map<string, number>();
  for (const id of Object.keys(state.players)) {
    gamesPlayed[id] = 0;
    roundsPresent[id] = 0;
    doubleSits[id] = 0;
  }
  const bump = (map: Map<string, number>, key: string): void => {
    map.set(key, (map.get(key) ?? 0) + 1);
  };
  let previousSatOut = new Set<PlayerId>();
  let playedLast = new Set<PlayerId>();
  let satOutLast = new Set<PlayerId>();
  for (const round of state.rounds) {
    playedLast = new Set<PlayerId>();
    satOutLast = new Set<PlayerId>();
    for (const court of round.lineup.courts) {
      for (const id of [...court.a, ...court.b]) {
        gamesPlayed[id] = (gamesPlayed[id] ?? 0) + 1;
        roundsPresent[id] = (roundsPresent[id] ?? 0) + 1;
        playedLast.add(id);
      }
      bump(partners, pairKey(court.a[0], court.a[1]));
      bump(partners, pairKey(court.b[0], court.b[1]));
      for (const x of court.a) for (const y of court.b) bump(opponents, pairKey(x, y));
    }
    for (const id of round.lineup.sittingOut) {
      roundsPresent[id] = (roundsPresent[id] ?? 0) + 1;
      satOutLast.add(id);
      if (previousSatOut.has(id)) doubleSits[id] = (doubleSits[id] ?? 0) + 1;
    }
    previousSatOut = satOutLast;
  }
  return { gamesPlayed, roundsPresent, playedLast, satOutLast, doubleSits, partners, opponents };
}

/** One player's view of a game they were in. */
export interface PlayerGame {
  roundNo: number;
  court: number;
  partner: PlayerId;
  opponents: Pair;
  /** My team's points, then theirs; null while the score is owed. */
  score: [number, number] | null;
  /** true won, false lost, null owed or voided. */
  won: boolean | null;
  voided: boolean;
  eventId: string | null;
}

/** Every game a player has been in, oldest first. */
export function playerGames(state: TournamentState, id: PlayerId): PlayerGame[] {
  const out: PlayerGame[] = [];
  for (const r of state.rounds) {
    for (const g of r.games) {
      const onA = g.a.includes(id);
      const onB = g.b.includes(id);
      if (!onA && !onB) continue;
      const mine = onA ? g.a : g.b;
      const theirs = onA ? g.b : g.a;
      const partner = mine[0] === id ? mine[1] : mine[0];
      const score: [number, number] | null = g.score ? (onA ? [g.score.a, g.score.b] : [g.score.b, g.score.a]) : null;
      out.push({
        roundNo: r.no,
        court: g.court,
        partner,
        opponents: theirs,
        score: g.voided ? null : score,
        won: g.voided || !score ? null : score[0] > score[1],
        voided: g.voided,
        eventId: g.eventId,
      });
    }
  }
  return out;
}

/** The last few results as letters, newest last: "W W L W". Owed and voided games are skipped. */
export function streak(games: PlayerGame[], last = 5): string {
  return games
    .filter((g) => g.won !== null)
    .slice(-last)
    .map((g) => (g.won ? "W" : "L"))
    .join(" ");
}

/** True once every court of the round has a score or is voided. */
export function allScoresIn(round: Round): boolean {
  return round.games.every((g) => g.voided || g.score !== null);
}

/**
 * True while the latest round is over and not one of its courts has a score or a void: the hall
 * has walked off, so the autopilot will not start another round by itself. One score or void for
 * that round lets it go, and an admin's Start always works. It does not look at the "start
 * rounds by themselves" switch, which is its own reason for nothing starting.
 */
export function heldForScores(state: TournamentState): boolean {
  const round = currentRound(state);
  if (!round || !round.games.length) return false;
  if (state.clock.phase === "playing" && state.clock.roundNo === round.no) return false;
  return round.games.every((g) => !g.voided && g.score === null);
}

/**
 * After a late score or void let the hold go: the time before which the next round will not
 * start by itself, one changeover after that score went in. Null when there is nothing to wait
 * for: a round on the clock, a round that was never held, or an older log with no stamp (which
 * reads as long ago). An admin's Start does not wait for it.
 */
export function startsByItselfAt(state: TournamentState): number | null {
  const round = currentRound(state);
  if (!round || state.clock.phase !== "idle" || round.releasedAt === undefined) return null;
  return round.releasedAt + state.config.changeoverMs;
}

/**
 * Between rounds (decision 41): which courts of the round just played are ready for the next. A
 * court is ready once its final score is in, from its tablet or an admin, or its game was voided.
 * A court closed since (more than the courts in use) does not count either way. Null while a
 * round is being played, and before the first.
 */
export function courtsReady(state: TournamentState): { roundNo: number; ready: number; of: number; waiting: number[] } | null {
  const round = currentRound(state);
  if (!round || (state.clock.phase === "playing" && state.clock.roundNo === round.no)) return null;
  const open = round.games.filter((g) => g.court <= state.config.courts);
  const waiting = open.filter((g) => !g.voided && g.score === null).map((g) => g.court);
  return { roundNo: round.no, ready: open.length - waiting.length, of: open.length, waiting };
}

/** Games still owed, oldest round first. */
export function owedScores(state: TournamentState): { roundNo: number; court: number }[] {
  const owed: { roundNo: number; court: number }[] = [];
  for (const round of state.rounds) {
    for (const game of round.games) {
      if (!game.voided && game.score === null) owed.push({ roundNo: round.no, court: game.court });
    }
  }
  return owed;
}

/**
 * The name to show for a player on a given screen. The one place that decides it, so "nickname
 * on the wall, real name on the tablets" (decision 27) is a change here and nowhere else.
 * Until players have nicknames it is the real name everywhere.
 */
export function displayName(player: Player | undefined, where: Where): string {
  if (!player) return "?";
  if (where === "tablet" || where === "admin") return player.name;
  return player.dub?.nickname ?? player.nickname ?? player.name;
}

/** The nickname showing on the wall, when it differs from the real name: the small second line on a tablet. */
export function aliasOf(player: Player | undefined): string | null {
  if (!player) return null;
  const alias = player.dub?.nickname ?? player.nickname;
  return alias && alias !== player.name ? alias : null;
}

/** True once a player has done what joining asks: a picture, or an admin's wave through. */
export function hasJoined(player: Player | undefined): boolean {
  return Boolean(player && (player.photo > 0 || player.photoWaived));
}

/** The loser's stamp: true while the last game they finished was a loss. */
export function lostLast(state: TournamentState, id: PlayerId): boolean {
  const done = playerGames(state, id).filter((g) => g.won !== null);
  const last = done[done.length - 1];
  return Boolean(last && last.won === false);
}

export interface Nemesis {
  id: PlayerId;
  played: number;
  lost: number;
}

/** Who a player cannot beat: the opponent they have lost to most, at least twice, never having beaten them. */
export function nemesis(state: TournamentState, id: PlayerId): Nemesis | null {
  const tally = new Map<PlayerId, { played: number; lost: number; won: number }>();
  for (const g of playerGames(state, id)) {
    if (g.won === null) continue;
    for (const opp of g.opponents) {
      const t = tally.get(opp) ?? { played: 0, lost: 0, won: 0 };
      t.played += 1;
      if (g.won) t.won += 1;
      else t.lost += 1;
      tally.set(opp, t);
    }
  }
  let best: Nemesis | null = null;
  for (const [opp, t] of tally) {
    if (t.won > 0 || t.lost < 2 || !state.players[opp]) continue;
    if (!best || t.lost > best.lost || (t.lost === best.lost && opp < best.id)) best = { id: opp, played: t.played, lost: t.lost };
  }
  return best;
}

/** The first letter of a name, for a face slot with no picture yet (a whole emoji, never half of one). */
export function initialOf(name: string): string {
  return ([...name.trim()][0] ?? "?").toUpperCase();
}
